import React, { useState, useEffect, useCallback } from 'react';
import {
  Cpu, Radio, Eye, Key, FileText, Bot, BarChart3,
  Terminal, Search, Loader2, AlertTriangle, Check,
  RefreshCw, Shield, Zap
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { confirmDialog } from '../ConfirmDialog';
import './BotdoxIntegration.css';

const SECTIONS = [
  {
    id: 'mcp',
    title: 'MCP Server',
    icon: Cpu,
    color: '#3b82f6',
    description: 'Model Context Protocol server for AI agent tool access',
    toggles: [
      {
        key: 'mcp_enabled',
        label: 'MCP Server',
        desc: 'Enable stdio MCP transport for AI agents (13 tools)',
        icon: Cpu,
        color: '#3b82f6',
      },
    ],
  },
  {
    id: 'discovery',
    title: 'Discovery Layer',
    icon: Search,
    color: '#8b5cf6',
    description: 'Make TechStack discoverable by AI agents',
    toggles: [
      {
        key: 'discovery_enabled',
        label: 'Discovery (master)',
        desc: 'Enable all discovery endpoints',
        icon: Search,
        color: '#8b5cf6',
      },
      {
        key: 'llms_txt_enabled',
        label: '/llms.txt',
        desc: 'Agent-readable capability summary',
        icon: FileText,
        color: '#8b5cf6',
        parent: 'discovery_enabled',
      },
      {
        key: 'ai_agent_json_enabled',
        label: '/.well-known/ai-agent.json',
        desc: 'Machine-readable manifest with tools and auth info',
        icon: FileText,
        color: '#8b5cf6',
        parent: 'discovery_enabled',
      },
      {
        key: 'robots_txt_enabled',
        label: '/robots.txt',
        desc: 'AI-friendly crawler rules (GPTBot, ClaudeBot, PerplexityBot)',
        icon: Bot,
        color: '#8b5cf6',
        parent: 'discovery_enabled',
      },
    ],
  },
  {
    id: 'attribution',
    title: 'Attribution & Analytics',
    icon: Eye,
    color: '#f59e0b',
    description: 'Track agent interactions and bot detection',
    toggles: [
      {
        key: 'attribution_enabled',
        label: 'Attribution Tracking',
        desc: 'Parse X-Agent-* headers on every request',
        icon: Eye,
        color: '#f59e0b',
      },
      {
        key: 'bot_detection_enabled',
        label: 'Bot Detection',
        desc: 'Identify GPTBot, ClaudeBot, PerplexityBot from User-Agent',
        icon: Bot,
        color: '#f59e0b',
        parent: 'attribution_enabled',
      },
      {
        key: 'analytics_enabled',
        label: 'Agent Analytics API',
        desc: '/api/analytics/* endpoints for agent usage dashboards',
        icon: BarChart3,
        color: '#f59e0b',
        parent: 'attribution_enabled',
      },
      {
        key: 'cli_attribution_tracking',
        label: 'CLI Attribution',
        desc: 'Track ts CLI requests via X-Agent-Client headers',
        icon: Terminal,
        color: '#f59e0b',
        parent: 'attribution_enabled',
      },
    ],
  },
  {
    id: 'auth',
    title: 'Agent Authentication',
    icon: Key,
    color: '#22c55e',
    description: 'API key management for programmatic access',
    toggles: [
      {
        key: 'api_keys_enabled',
        label: 'API Key Auth',
        desc: 'Allow bdx_ts_* API keys for agent/CLI authentication',
        icon: Key,
        color: '#22c55e',
      },
    ],
  },
];

function BotdoxIntegration() {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/api/integration/status');
      setSettings(data.settings || {});
      setStats(data.stats || {});
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggle = async (key, value) => {
    const prev = { ...settings };
    const updated = { ...settings, [key]: value };

    // If disabling a parent, disable children too
    const cascaded = [];
    for (const section of SECTIONS) {
      for (const toggle of section.toggles) {
        if (toggle.parent === key && !value) {
          if (settings[toggle.key]) cascaded.push(toggle.label);
          updated[toggle.key] = false;
        }
      }
    }

    // Auto-saved kill switches confirm before they sever anything (slice 4):
    // API-key auth off cuts every key-authenticated integration (GAS, CLI,
    // agents) at once; a parent toggle silently dragging live children down
    // is called out instead of happening wordlessly.
    if (!value && key === 'api_keys_enabled') {
      const ok = await confirmDialog({
        title: 'Turn off API key auth',
        message: 'Every integration that authenticates with an API key — GAS, CLI, agents — loses access immediately. Turn it off?',
        confirmLabel: 'Turn off',
      });
      if (!ok) return;
    } else if (cascaded.length > 0) {
      const ok = await confirmDialog({
        title: 'Turn off with dependents',
        message: `Turning this off also disables: ${cascaded.join(', ')}. Continue?`,
        confirmLabel: 'Turn off',
      });
      if (!ok) return;
    }

    setSettings(updated);
    setSaving(true);
    setSuccess(null);

    try {
      await apiClient.put('/api/integration/settings', { settings: updated });
      setSuccess(`${key.replace(/_/g, ' ')} ${value ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setSettings(prev); // Rollback
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bi-loading">
        <Loader2 size={32} className="bi-spin" />
        <span>Loading integration settings...</span>
      </div>
    );
  }

  const enabledCount = settings
    ? Object.values(settings).filter(v => v === true).length
    : 0;
  const totalCount = settings ? Object.keys(settings).length : 0;

  return (
    <div className="bi-container">
      {/* Header */}
      <div className="bi-header">
        <div className="bi-header-left">
          <Zap size={24} strokeWidth={2} style={{ color: '#3b82f6' }} />
          <div>
            <h1>Botdox Integration</h1>
            <p className="bi-subtitle">
              Control MCP server, agent discovery, attribution tracking, and API key auth
            </p>
          </div>
        </div>
        <button
          onClick={fetchData}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0.5rem',
            background: 'var(--color-surface-hover, rgba(255,255,255,0.05))',
            border: '1px solid var(--color-border, rgba(255,255,255,0.1))',
            borderRadius: '0.5rem',
            color: 'var(--color-text-muted, #94a3b8)',
            cursor: 'pointer',
          }}
          title="Refresh"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Error/Success */}
      {error && (
        <div className="bi-error">
          <AlertTriangle size={14} />
          {error}
          <button
            onClick={() => setError(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
          >
            <span style={{ fontSize: '0.8rem' }}>Dismiss</span>
          </button>
        </div>
      )}
      {success && (
        <div className="bi-success">
          <Check size={14} />
          {success}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="bi-stats">
          <div className="bi-stat-card">
            <div className="bi-stat-icon" style={{ background: 'rgba(59,130,246,0.12)' }}>
              <Shield size={20} style={{ color: '#3b82f6' }} />
            </div>
            <div>
              <div className="bi-stat-value">{enabledCount}/{totalCount}</div>
              <div className="bi-stat-label">Features enabled</div>
            </div>
          </div>
          <div className="bi-stat-card">
            <div className="bi-stat-icon" style={{ background: 'rgba(34,197,94,0.12)' }}>
              <Key size={20} style={{ color: '#22c55e' }} />
            </div>
            <div>
              <div className="bi-stat-value">{stats.api_keys_active || 0}</div>
              <div className="bi-stat-label">Active API keys</div>
            </div>
          </div>
          <div className="bi-stat-card">
            <div className="bi-stat-icon" style={{ background: 'rgba(245,158,11,0.12)' }}>
              <Eye size={20} style={{ color: '#f59e0b' }} />
            </div>
            <div>
              <div className="bi-stat-value">{stats.events_24h || 0}</div>
              <div className="bi-stat-label">Agent events (24h)</div>
            </div>
          </div>
        </div>
      )}

      {/* Feature Sections */}
      {SECTIONS.map((section) => {
        const SectionIcon = section.icon;
        const sectionActive = section.toggles.some(t => settings[t.key]);

        return (
          <div className="bi-section" key={section.id}>
            <div className="bi-section-header">
              <div className="bi-section-title">
                <SectionIcon size={18} style={{ color: section.color }} />
                <span>{section.title}</span>
                <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', fontWeight: 400 }}>
                  {section.description}
                </span>
              </div>
              <span className={`bi-section-badge ${sectionActive ? 'bi-badge-active' : 'bi-badge-disabled'}`}>
                {sectionActive ? 'Active' : 'Off'}
              </span>
            </div>

            <div className="bi-toggle-list">
              {section.toggles.map((toggle) => {
                const ToggleIcon = toggle.icon;
                const isDisabledByParent = toggle.parent && !settings[toggle.parent];
                const isChecked = settings[toggle.key] || false;

                return (
                  <div className="bi-toggle-row" key={toggle.key}>
                    <div className="bi-toggle-info">
                      <div
                        className="bi-toggle-icon"
                        style={{
                          background: `${toggle.color}15`,
                          opacity: isDisabledByParent ? 0.4 : 1,
                        }}
                      >
                        <ToggleIcon size={16} style={{ color: toggle.color }} />
                      </div>
                      <div style={{ opacity: isDisabledByParent ? 0.5 : 1 }}>
                        <div className="bi-toggle-label">{toggle.label}</div>
                        <div className="bi-toggle-desc">{toggle.desc}</div>
                      </div>
                    </div>

                    <label className="bi-switch">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={saving || isDisabledByParent}
                        onChange={(e) => handleToggle(toggle.key, e.target.checked)}
                      />
                      <span className="bi-switch-slider" />
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default BotdoxIntegration;
